import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff, Landmark, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Card, Chip, Dialog, EmptyState, SkeletonList, toast } from '@/ui';
import { useSessionStore } from '@/auth/session.store';
import { copyText } from '@/lib/clipboard';
import { formatIban, maskIban } from '@/lib/iban-format';
import { AccountFormDrawer, type AccountFormValues } from './AccountFormDrawer';
import type { BankAccount } from './api';
import { bankInitial, bankLogo, BANK_LIST, TRY_CURRENCY } from './banks';
import { groupAccountsByBank } from './group-accounts';
import { useAccountsQuery, useDeleteAccountMutation, useSaveAccountMutation } from './queries';
import styles from './Accounts.module.css';

/**
 * BANKA HESAPLARI (IBAN defteri) — musteri ve buro icin AYNI ekran.
 *
 * Iki menu kimligine (customer.accounts / exchange.accounts) tek bilesen
 * baglanir; ikisi arasindaki tek fark YETKI ve o da veriden okunuyor. Iki kopya
 * yazilsaydi birinde duzeltilen bir IBAN hatasi otekinde yasamaya devam ederdi.
 *
 * ─────────────────────── IBAN'A DAVRANIS ───────────────────────
 * IBAN bu urundeki en hassas alan; sunucu onu talep/teklif govdelerinde zaten
 * asgariye indiriyor. Bu ekran o kazanci geri vermez:
 *   * Liste satirlari MASKELI gosterir (bkz. src/lib/iban-format.ts).
 *   * Tam deger yalniz kullanici acikca "goster" dediginde ortaya cikar ve o
 *     karar SATIR BAZINDA, kalici degil — sayfa yenilendiginde geri kapanir.
 *   * Hicbir yere loglanmaz, hicbir sorgu dizesine konmaz.
 *
 * ─────────────────────── KIM DUZENLEYEBILIR ───────────────────────
 * OKUMA: rota buro tarafinda `viewAccounts` ile korunuyor (router.tsx).
 *
 * YAZMA (2026-08-30'da DEGISTI): organizasyon hesaplarini — musteri ve buro ayrimi olmadan —
 * yalniz super_admin tanimlar, cunku IBAN'larin bankayla bir onay sureci var. Organizasyon
 * uyesi icin bu ekran SALT OKUNURDUR ve sunucu da ayni cevabi verir (403
 * ACCOUNT_MANAGED_BY_PLATFORM). Ayrim YETKIDE DEGIL, hesabin kime ait olduguna dair:
 * yonetici de personel de duzenleyemez.
 *
 * `manageAccounts` yetkisi artik ATANAMAZ (is_org_grantable=false) ve bu ekran ona
 * BAKMAZ — elde kalmis bir grant, sunucunun uygulamadigi bir kurali gosterirdi.
 *
 * Organizasyonu OLMAYAN kullanici kendi hesaplarinin sahibi olmaya devam eder; musteriler
 * de org uyesi oldugu icin bu yol bugun pratikte yalniz super_admin'e kaliyor.
 */

/** Yeni hesap formunun acilis degerleri. Ilk banka + USD, mobil ile ayni. */
const emptyFormValues = (): AccountFormValues => ({
  bankCode: BANK_LIST[0]?.code ?? '',
  currency: 'USD',
  accountName: '',
  iban: '',
});

type DrawerState = Readonly<{
  /** Her acilista artan sayac: form agacini `key` ile bastan kurar. */
  session: number;
  editingId: string | undefined;
  values: AccountFormValues;
  lockCurrencyToTry: boolean;
}>;

export function AccountsPage() {
  const { t } = useTranslation();
  const snapshot = useSessionStore((state) => state.snapshot);

  const isOrgMember = Boolean(snapshot?.organization);
  /**
   * IBAN'LAR PLATFORM YONETIMINDE (2026-08-30 urun karari).
   *
   * Organizasyon hesaplari — musteri ve buro ayrimi olmadan — yalniz super_admin tarafindan
   * tanimlanir, cunku hesaplarin bankayla bir onay sureci var. Sunucu bunu KOSULSUZ uyguluyor:
   * `POST /v1/accounts` org uyesine 403 ACCOUNT_MANAGED_BY_PLATFORM doner (yetki kontrolu
   * DEGIL — elde kalmis bir `manageAccounts` grant'i politikayi delerdi; o yetki de artik
   * atanamaz: is_org_grantable=false).
   *
   * Bu yuzden `hasPermission(snapshot, 'manageAccounts')' ARTIK SORULMUYOR: sorulsaydi istemci,
   * sunucunun uygulamadigi bir kural gosterir ve calismayacak bir dugme vaat ederdi.
   * Organizasyonu olmayan kullanici (bugun pratikte yalniz super_admin) kendi hesaplarinin
   * sahibi olmaya devam eder.
   */
  const canManage = !isOrgMember;

  const accountsQuery = useAccountsQuery();
  const saveMutation = useSaveAccountMutation();
  const deleteMutation = useDeleteAccountMutation();

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  /**
   * Cekmece acilis sayaci. Zaman damgasi DEGIL: `Date.now()` saf olmayan bir
   * cagri (react-hooks/purity) ve ayni milisaniyede iki acilis ayni anahtari
   * uretebilirdi. Artan sayac ikisini de cozer.
   */
  const drawerSession = useRef(0);
  const nextDrawerSession = (): number => {
    drawerSession.current += 1;
    return drawerSession.current;
  };
  const [pendingDelete, setPendingDelete] = useState<BankAccount | null>(null);
  // Acikca gosterilmesi istenen satirlar. Kalici DEGIL: yeniden yuklemede
  // hepsi tekrar maskelenir.
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());

  const openCreate = (): void => {
    setDrawer({
      session: nextDrawerSession(),
      editingId: undefined,
      values: emptyFormValues(),
      lockCurrencyToTry: false,
    });
  };

  const openTryShortcut = (bankCode: string): void => {
    setDrawer({
      session: nextDrawerSession(),
      editingId: undefined,
      values: { ...emptyFormValues(), bankCode, currency: TRY_CURRENCY },
      lockCurrencyToTry: true,
    });
  };

  const openEdit = (account: BankAccount): void => {
    setDrawer({
      session: nextDrawerSession(),
      editingId: account.id,
      values: {
        bankCode: account.bankCode,
        currency: account.currency,
        accountName: account.accountName,
        // Formda bosluklu gosterilir; gonderimde normalize edilir.
        iban: formatIban(account.iban),
      },
      lockCurrencyToTry: false,
    });
  };

  const closeDrawer = (): void => {
    setDrawer(null);
  };

  const toggleReveal = (id: string): void => {
    setRevealed((current) => {
      const next = new Set(current);
      // String(a) === String(b): kimlikler pg'den STRING geliyor, sayiya
      // cevrilmez. Set zaten dize anahtarli.
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyIban = (iban: string): void => {
    // Panoya yazmak izne ve guvenli baglama bagli; olmadigi ortamda sessizce
    // basarisiz olmak yerine kullaniciya soyluyoruz. Koruma tek yerde: lib/clipboard.ts
    // (BankPanel de ayni kapiyi kullaniyor).
    void copyText(iban).then((copied) => {
      toast(
        copied
          ? { message: t('task.ibanCopied'), tone: 'success' }
          : { message: t('accounts.copyFailed'), tone: 'danger' },
      );
    });
  };

  const submitForm = (input: Parameters<typeof saveMutation.mutate>[0]): void => {
    const editingId = drawer?.editingId;
    saveMutation.mutate(
      // Ayni uc hem olusturur hem gunceller; ayirici govdedeki `id`.
      editingId === undefined ? input : { ...input, id: editingId },
      {
        onSuccess: () => {
          closeDrawer();
          toast({ message: t('accounts.saved'), tone: 'success' });
        },
        onError: () => {
          toast({ message: t('accounts.saveFailed'), tone: 'danger' });
        },
      },
    );
  };

  const confirmDelete = (): void => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete, {
      onSuccess: () => {
        setPendingDelete(null);
        toast({ message: t('accounts.deleted'), tone: 'success' });
      },
      onError: () => {
        toast({ message: t('accounts.deleteFailed'), tone: 'danger' });
      },
    });
  };

  const groups = groupAccountsByBank(accountsQuery.data ?? []);

  return (
    <section className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('accounts.pageName')}</h1>
        {canManage ? (
          <Button iconLeft={<Plus size={16} aria-hidden="true" />} onClick={openCreate}>
            {t('accounts.addAccount')}
          </Button>
        ) : null}
      </header>

      {/* Yalniz okuyabilen personel, dugmelerin NEDEN olmadigini bilmeli;
          aksi halde ekran "bozuk" gorunur. */}
      {!canManage ? <p className={styles.readOnlyNote}>{t('accounts.readOnly')}</p> : null}

      {accountsQuery.isPending ? <SkeletonList rows={3} /> : null}

      {accountsQuery.isError ? (
        <EmptyState
          tone="error"
          title={t('accounts.loadFailed')}
          description={t('list.error.generic')}
          action={
            <Button
              tone="neutral"
              onClick={() => {
                void accountsQuery.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      ) : null}

      {accountsQuery.isSuccess && groups.length === 0 ? (
        <EmptyState
          icon={<Landmark size={28} aria-hidden="true" />}
          title={t('accounts.empty')}
          description={t('accounts.emptyDescription')}
          action={
            canManage ? (
              <Button iconLeft={<Plus size={16} aria-hidden="true" />} onClick={openCreate}>
                {t('accounts.addAccount')}
              </Button>
            ) : null
          }
        />
      ) : null}

      <div className={styles.groups}>
        {groups.map((group) => (
          <Card key={group.bankCode} className={styles.bankCard}>
            <div className={styles.bankHeader}>
              <BankBadge code={group.bankCode} displayName={group.displayName} />
              <h2 className={styles.bankName}>{group.displayName}</h2>
            </div>

            <ul className={styles.accountList}>
              {group.accounts.map((account) => (
                <li key={account.id} className={styles.accountRow}>
                  <div className={styles.accountInfo}>
                    <div className={styles.accountTop}>
                      <Chip tone="primary" size="sm">
                        {account.currency}
                      </Chip>
                      <span className={styles.accountName}>{account.accountName}</span>
                    </div>
                    <p className={styles.iban}>
                      {revealed.has(account.id) ? formatIban(account.iban) : maskIban(account.iban)}
                    </p>
                  </div>

                  <div className={styles.rowActions}>
                    <Button
                      tone="ghost"
                      size="sm"
                      aria-label={
                        revealed.has(account.id) ? t('accounts.hideIban') : t('accounts.showIban')
                      }
                      aria-pressed={revealed.has(account.id)}
                      onClick={() => {
                        toggleReveal(account.id);
                      }}
                      iconLeft={
                        revealed.has(account.id) ? (
                          <EyeOff size={16} aria-hidden="true" />
                        ) : (
                          <Eye size={16} aria-hidden="true" />
                        )
                      }
                    />
                    <Button
                      tone="ghost"
                      size="sm"
                      aria-label={t('accounts.copyIban')}
                      onClick={() => {
                        copyIban(account.iban);
                      }}
                      iconLeft={<Copy size={16} aria-hidden="true" />}
                    />
                    {canManage ? (
                      <>
                        <Button
                          tone="ghost"
                          size="sm"
                          aria-label={t('accounts.editAccount')}
                          onClick={() => {
                            openEdit(account);
                          }}
                          iconLeft={<Pencil size={16} aria-hidden="true" />}
                        />
                        <Button
                          tone="danger"
                          size="sm"
                          aria-label={t('accounts.deleteTitle')}
                          onClick={() => {
                            setPendingDelete(account);
                          }}
                          iconLeft={<Trash2 size={16} aria-hidden="true" />}
                        />
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            {/* TRY hesabi olmayan banka icin kisayol: takasin karsi bacagi
                neredeyse her zaman TRY, eksikligi ancak islem ortasinda fark
                ediliyor. */}
            {!group.hasTry && canManage ? (
              <Button
                tone="ghost"
                size="sm"
                className={styles.tryShortcut}
                iconLeft={<Plus size={14} aria-hidden="true" />}
                onClick={() => {
                  openTryShortcut(group.bankCode);
                }}
              >
                {t('accounts.addTry')}
              </Button>
            ) : null}
          </Card>
        ))}
      </div>

      {drawer ? (
        <AccountFormDrawer
          // Her acilista yeni `key`: form agaci bastan kurulur, onceki
          // kaydin degerleri sizmaz (bkz. AccountFormDrawer basligi).
          key={drawer.session}
          open
          onClose={closeDrawer}
          editingId={drawer.editingId}
          initialValues={drawer.values}
          lockCurrencyToTry={drawer.lockCurrencyToTry}
          busy={saveMutation.isPending}
          onSubmit={submitForm}
        />
      ) : null}

      {/* YUMUSAK SILME. Metin bunu "listenizden kaldirilacak" diye anlatir;
          "kalici olarak silinecek" demek yanlis olurdu — satir duruyor. */}
      <Dialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('accounts.deleteTitle')}
        description={t('accounts.deleteDescription')}
        confirmLabel={t('accounts.delete')}
        cancelLabel={t('actions.cancel')}
        busy={deleteMutation.isPending}
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </section>
  );
}

/**
 * Banka rozeti. Logo yoksa (katalogda olmayan bir `accountCode`) BAS HARFLI
 * rozete duser — kirik resim ikonu ya da bos bir kare yerine.
 */
function BankBadge({ code, displayName }: { code: string; displayName: string }) {
  const logo = bankLogo(code);
  if (!logo) {
    return (
      <span className={styles.bankInitial} aria-hidden="true">
        {bankInitial(displayName)}
      </span>
    );
  }
  // alt="" : banka adi hemen yanindaki baslikta zaten yaziyor; ikinci kez
  // okunmasi ekran okuyucuda gurultu.
  return <img className={styles.bankLogo} src={logo} alt="" />;
}
